import { colors } from '@/constants/colors';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadItem from './Table/HeadItem';
import DataItem from './Table/DataItem';
import { currencyFormat } from '@/utils/currencyFormatter';
import IconsLib from '@/utils/IconsLib';
import { DropdownActionType } from '@/types/shared';
import ImageUI from './UI/ImageUI';

/**
 * The wallet mark for an online sale. Every e-wallet payment used to be GCash,
 * so the row simply drew the GCash logo; now Maya and QR Ph settle here too
 * and a Maya sale must not wear GCash's badge. Falls back to GCash for rows
 * written before the channel was recorded.
 */
const PAY_ICONS: Record<string, string> = {
   GCASH: '/gcashpay.webp',
   MAYA: '/pay/maya.svg',
   QRPH: '/pay/qrph.svg',
   GRABPAY: '/pay/grab.svg',
   SHOPEEPAY: '/pay/shopee.svg',
   CARDS: '/pay/visa.svg',
};
function payIconFor(item: unknown): { src: string; alt: string } {
   const code = String((item as { paymentChannel?: unknown })?.paymentChannel || '').toUpperCase();
   return { src: PAY_ICONS[code] || '/gcashpay.webp', alt: code ? code.toLowerCase() : 'gcash' };
}
import { bankData } from '@/utils/bankData';
import Moment from 'react-moment';
import 'moment-timezone';
// import { DropdownActionType } from '@/pages/Admin/Stores';

type TableItem = {
   _id: string;
   status?: string;
   payoutMethod?: string;
};

interface CheckboxSelection {
   selected: Set<string>;
   onToggle: (id: string) => void;
   onToggleAll: () => void;
}

interface Props<T extends TableItem> {
   data: T[];
   headData: Array<{
      type: string;
      value: string;
   }>;
   type: string;
   grid: string;
   dropdownData?: DropdownActionType<T>[];
   onRowClick?: (item: T) => void;
   onSetup?: (item: T) => void;
   checkboxSelection?: CheckboxSelection;
}

const Table = <T extends TableItem>({
   headData,
   data,
   type,
   grid,
   dropdownData,
   onRowClick,
   onSetup,
   checkboxSelection,
}: Props<T>) => {
   const [itemChecked, setItemChecked] = useState<T[]>([]);
   const [dataInSearch, setDataInSearch] = useState<T[]>([]);

   useEffect(() => {
      setDataInSearch(data);
   }, [type, data]);

   // console.log(data);

   const handleItemChecked = (item: T) => {
      if (itemChecked.some((i) => i._id === item._id)) {
         const result = itemChecked.filter((i) => i._id !== item._id);
         setItemChecked(result);
      } else {
         setItemChecked([...itemChecked, item]);
      }
   };

   const handleCheckAll = () => {
      if (data.length === itemChecked.length) {
         setItemChecked([]);
      } else {
         const checkedItems: T[] = []; // Explicitly typed
         setItemChecked(checkedItems);
      }
   };

   return (
      <Wrapper>
         <Content>
         <HeadWrapper $col={grid}>
            {headData.map((item, index) => {
               if (item.type === 'checkbox' && checkboxSelection) {
                  const allChecked = data.length > 0 && checkboxSelection.selected.size === data.length;
                  return (
                     <CheckboxCell key={index}>
                        <Checkbox
                           type="checkbox"
                           checked={allChecked}
                           onChange={checkboxSelection.onToggleAll}
                        />
                     </CheckboxCell>
                  );
               }
               return (
                  item.value !== null && (
                     <HeadItem
                        key={index}
                        type={item.type}
                        value={item.value}
                        checkAll={itemChecked.length === data.length}
                        handleCheckAll={handleCheckAll}
                     />
                  )
               );
            })}
         </HeadWrapper>

         {dataInSearch.map((item, index) => {
            return (
               <div key={index}>
                  {type === 'store' && (
                     <DesktopRow
                        selected={itemChecked.some((i) => i._id === item._id)}
                        $col={grid}
                     >
                        <StoreLogo>
                           {'logo' in item && (item as { logo?: string }).logo ? (
                              <StoreLogoImg src={(item as { logo: string }).logo} alt="" />
                           ) : (
                              <StoreLogoPlaceholder>
                                 <IconsLib icon="store-3-line" size="18px" color={colors.GRAY8} />
                              </StoreLogoPlaceholder>
                           )}
                        </StoreLogo>
                        <div onClick={() => handleItemChecked(item)}>
                           {'storename' in item && (
                              <DataItem
                                 type="title"
                                 $textbold={true}
                                 value={
                                    (item as { storename: string }).storename
                                 }
                              />
                           )}
                        </div>

                        <a
                           href={(item as Record<string, string>).storeDomain}
                           target="_blank"
                           rel="noopener noreferrer"
                        >
                           {'storeDomain' in item && (
                              <DataItem
                                 type="link"
                                 $texttransform="lowercase"
                                 value={
                                    (item as { storeDomain: string })
                                       .storeDomain
                                 }
                              />
                           )}
                        </a>
                        {'availableVouchers' in item && (
                           <DataItem
                              type="normal"
                              value={(
                                 item as { availableVouchers: number }
                              ).availableVouchers.toString()}
                           />
                        )}
                        {'soldVouchers' in item && (
                           <DataItem
                              type="normal"
                              value={(
                                 item as { soldVouchers: number }
                              ).soldVouchers.toString()}
                           />
                        )}
                        {'totalSales' in item && (
                           <DataItem
                              type="normal"
                              value={currencyFormat(
                                 (item as { totalSales: number }).totalSales,
                                 true
                              )}
                           />
                        )}
                        {onSetup && (
                           <SetupBtn onClick={(e) => { e.stopPropagation(); onSetup(item); }}>
                              Setup
                           </SetupBtn>
                        )}
                        <DataItem
                           type="more-action"
                           value={(item as { _id: string })._id.toString()}
                           dropdownData={dropdownData}
                           selected={item}
                        />
                     </DesktopRow>
                  )}

                  {type === 'store' && (() => {
                     const s = item as unknown as {
                        _id: string;
                        logo?: string;
                        storename?: string;
                        storeDomain?: string;
                        availableVouchers?: number;
                        soldVouchers?: number;
                        totalSales?: number;
                     };
                     return (
                        <MobileCard>
                           <MobileCardTop>
                              <StoreAvatar>
                                 {s.logo ? (
                                    <StoreLogoImg src={s.logo} alt="" />
                                 ) : (
                                    <StoreLogoPlaceholder>
                                       <IconsLib icon="store-3-line" size="16px" color={colors.GRAY8} />
                                    </StoreLogoPlaceholder>
                                 )}
                              </StoreAvatar>
                              <MobileCodeBlock>
                                 <MobileCode>{s.storename ?? ''}</MobileCode>
                                 {s.storeDomain && (
                                    <MobileDomain
                                       href={s.storeDomain}
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       onClick={(e) => e.stopPropagation()}
                                    >
                                       {s.storeDomain}
                                    </MobileDomain>
                                 )}
                              </MobileCodeBlock>
                           </MobileCardTop>
                           <MobileStatsRow>
                              <Stat>
                                 <StatLabel>Available</StatLabel>
                                 <StatValue>{s.availableVouchers ?? 0}</StatValue>
                              </Stat>
                              <Stat>
                                 <StatLabel>Sold</StatLabel>
                                 <StatValue>{s.soldVouchers ?? 0}</StatValue>
                              </Stat>
                              <Stat $right>
                                 <StatLabel>Total Sales</StatLabel>
                                 <MobilePrice>
                                    {currencyFormat(s.totalSales ?? 0, true)}
                                 </MobilePrice>
                              </Stat>
                           </MobileStatsRow>
                           {onSetup && (
                              <SetupBtnFull onClick={(e) => { e.stopPropagation(); onSetup(item); }}>
                                 Setup
                              </SetupBtnFull>
                           )}
                           <MobileActionSlot>
                              <DataItem
                                 type="more-action"
                                 value={s._id.toString()}
                                 dropdownData={dropdownData}
                                 selected={item}
                              />
                           </MobileActionSlot>
                        </MobileCard>
                     );
                  })()}

                  {type === 'billing' && (
                     <DesktopRow
                        selected={itemChecked.some((i) => i._id === item._id)}
                        $col={grid}
                     >
                        {'createdAt' in item && (
                           <DataItem
                              type="date"
                              value={(item as { createdAt: string }).createdAt}
                           />
                        )}
                        {'store' in item && (
                           <DataItem
                              type="normal"
                              value={(item as { store: string }).store}
                           />
                        )}
                        {'subscription' in item && (
                           <DataItem
                              type="normal"
                              value={
                                 (item as { subscription: string })
                                    .subscription + ' months'
                              }
                           />
                        )}
                        {'status' in item && (
                           <DataItem
                              type="badge"
                              // value={(item as { status: string }).status}
                              value={{
                                 color:
                                    (item as { status: string }).status ===
                                    'success'
                                       ? 'SUCCESS'
                                       : 'GRAY5', // Ensure color is included
                                 text:
                                    (item as { status: string }).status ===
                                    'pending'
                                       ? 'Pending'
                                       : 'Success',
                              }}
                           />
                        )}
                        {'amount' in item && (
                           <DataItem
                              type="normal"
                              value={currencyFormat(
                                 (item as { amount: number }).amount,
                                 true
                              )}
                           />
                        )}
                        {/* {'paymentMethod' in item && (
                           <DataItem
                              type="normal"
                              value={
                                 (item as { paymentMethod: string })
                                    .paymentMethod
                              }
                           />
                        )} */}
                        <ImageUI
                           src={payIconFor(item).src}
                           alt={payIconFor(item).alt}
                           width="20px"
                           height="20px"
                        />
                        {item.status !== 'success' ? (
                           <DataItem
                              type="more-action"
                              value={(item as { _id: string })._id.toString()}
                              // extraValue={colors.GRAY4}
                              dropdownData={dropdownData}
                              selected={item}
                           />
                        ) : (
                           <>---</>
                        )}
                     </DesktopRow>
                  )}

                  {type === 'billing' && (() => {
                     const b = item as unknown as {
                        _id: string;
                        createdAt?: string;
                        store?: string;
                        subscription?: string;
                        status?: string;
                        amount?: number;
                     };
                     const isSuccess = b.status === 'success';
                     return (
                        <MobileCard>
                           <MobileCardTop>
                              <MobileCodeBlock>
                                 <MobileCode>{b.store ?? ''}</MobileCode>
                                 {b.subscription && (
                                    <MobileTitleSm>{b.subscription} months</MobileTitleSm>
                                 )}
                              </MobileCodeBlock>
                              <MobileStatusPill $variant={isSuccess ? 'success' : 'neutral'}>
                                 {b.status === 'pending' ? 'Pending' : 'Success'}
                              </MobileStatusPill>
                           </MobileCardTop>
                           <MobileCardMid>
                              <MobileMeta>
                                 <ImageUI src={payIconFor(b).src} alt={payIconFor(b).alt} width="18px" height="18px" />
                                 {b.createdAt && (
                                    <span>
                                       <Moment fromNow>{new Date(b.createdAt)}</Moment>
                                    </span>
                                 )}
                              </MobileMeta>
                              {b.amount !== undefined && (
                                 <MobilePrice>{currencyFormat(b.amount, true)}</MobilePrice>
                              )}
                           </MobileCardMid>
                           {b.status !== 'success' && (
                              <MobileActionSlot>
                                 <DataItem
                                    type="more-action"
                                    value={b._id.toString()}
                                    dropdownData={dropdownData}
                                    selected={item}
                                 />
                              </MobileActionSlot>
                           )}
                        </MobileCard>
                     );
                  })()}

                  {type === 'payout' && (
                     <DesktopRow
                        selected={itemChecked.some((i) => i._id === item._id)}
                        $col={grid}
                     >
                        {'createdAt' in item && (
                           <DataItem
                              type="date"
                              value={(item as { createdAt: string }).createdAt}
                           />
                        )}

                        {'payoutAccount' in item && (
                           <DataItem
                              type="normal"
                              value={
                                 (item as { payoutAccount: string })
                                    .payoutAccount
                              }
                           />
                        )}

                        {'accountNumber' in item && (
                           <DataItem
                              type="normal"
                              value={
                                 (item as { accountNumber: string })
                                    .accountNumber
                              }
                           />
                        )}
                        {'status' in item && (
                           <DataItem
                              type="badge"
                              // value={(item as { status: string }).status}
                              value={{
                                 color:
                                    (item as { status: string }).status ===
                                    'settled'
                                       ? 'SUCCESS'
                                       : (item as { status: string }).status ===
                                         'rejected'
                                       ? 'DANGER'
                                       : 'GRAY5', // Ensure color is included
                                 text:
                                    (item as { status: string }).status ===
                                    'processing'
                                       ? 'Processing'
                                       : (item as { status: string }).status ===
                                         'rejected'
                                       ? 'Rejected'
                                       : 'Settled',
                              }}
                           />
                        )}
                        {'amount' in item && (
                           <DataItem
                              type="normal"
                              value={currencyFormat(
                                 (item as { amount: number }).amount,
                                 true
                              )}
                           />
                        )}
                        {'netAmount' in item && (
                           <DataItem
                              type="normal"
                              value={currencyFormat(
                                 (item as { netAmount: number }).netAmount,
                                 true
                              )}
                           />
                        )}
                        {'disbursementFee' in item && (
                           <DataItem
                              type="normal"
                              value={currencyFormat(
                                 (item as { disbursementFee: number })
                                    .disbursementFee,
                                 true
                              )}
                           />
                        )}
                        {/* {'paymentMethod' in item && (
                           <DataItem
                              type="normal"
                              value={
                                 (item as { paymentMethod: string })
                                    .paymentMethod
                              }
                           />
                        )} */}
                        <ImageUI
                           src={
                              bankData.find(
                                 (bank) => bank._id === item.payoutMethod
                              )?.srcIcon || ''
                           }
                           alt="payment-method"
                           width="25px"
                           height="25px"
                        />
                        {item.status !== 'settled' &&
                        item.status !== 'rejected' ? (
                           <DataItem
                              type="more-action"
                              value={(item as { _id: string })._id.toString()}
                              // extraValue={colors.GRAY4}
                              dropdownData={dropdownData}
                              selected={item}
                           />
                        ) : (
                           <>---</>
                        )}
                     </DesktopRow>
                  )}

                  {type === 'payout' && (() => {
                     const p = item as unknown as {
                        _id: string;
                        createdAt?: string;
                        payoutAccount?: string;
                        accountNumber?: string;
                        status?: string;
                        amount?: number;
                        netAmount?: number;
                        disbursementFee?: number;
                        payoutMethod?: string;
                     };
                     const statusText =
                        p.status === 'processing'
                           ? 'Processing'
                           : p.status === 'rejected'
                           ? 'Rejected'
                           : 'Settled';
                     const variant: 'success' | 'danger' | 'neutral' =
                        p.status === 'settled'
                           ? 'success'
                           : p.status === 'rejected'
                           ? 'danger'
                           : 'neutral';
                     const bankIcon =
                        bankData.find((bk) => bk._id === p.payoutMethod)?.srcIcon || '';
                     const actionable = p.status !== 'settled' && p.status !== 'rejected';
                     return (
                        <MobileCard>
                           <MobileCardTop>
                              <MobileCodeBlock>
                                 <MobileCode>{p.payoutAccount ?? ''}</MobileCode>
                                 {p.accountNumber && (
                                    <MobileTitleSm>{p.accountNumber}</MobileTitleSm>
                                 )}
                              </MobileCodeBlock>
                              <MobileStatusPill $variant={variant}>
                                 {statusText}
                              </MobileStatusPill>
                           </MobileCardTop>
                           <MobileCardMid>
                              <MobileMeta>
                                 {bankIcon && (
                                    <ImageUI src={bankIcon} alt="bank" width="20px" height="20px" />
                                 )}
                                 {p.createdAt && (
                                    <span>
                                       <Moment fromNow>{new Date(p.createdAt)}</Moment>
                                    </span>
                                 )}
                              </MobileMeta>
                              {p.netAmount !== undefined && (
                                 <MobilePrice>{currencyFormat(p.netAmount, true)}</MobilePrice>
                              )}
                           </MobileCardMid>
                           {(p.amount !== undefined || p.disbursementFee !== undefined) && (
                              <MobileSubMeta>
                                 {p.amount !== undefined && (
                                    <span>Gross {currencyFormat(p.amount, true)}</span>
                                 )}
                                 {p.disbursementFee !== undefined && (
                                    <>
                                       <MobileDot>·</MobileDot>
                                       <span>Fee {currencyFormat(p.disbursementFee, true)}</span>
                                    </>
                                 )}
                              </MobileSubMeta>
                           )}
                           {actionable && (
                              <MobileActionSlot>
                                 <DataItem
                                    type="more-action"
                                    value={p._id.toString()}
                                    dropdownData={dropdownData}
                                    selected={item}
                                 />
                              </MobileActionSlot>
                           )}
                        </MobileCard>
                     );
                  })()}

                  {type === 'voucher' && (() => {
                     const v = item as unknown as {
                        _id: string;
                        code?: string;
                        store?: string;
                        price?: number;
                        sold?: boolean;
                        createdAt?: string;
                     };
                     const isChecked = checkboxSelection?.selected.has(v._id) ?? false;
                     const dateLabel = v.createdAt
                        ? new Date(v.createdAt).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—';
                     return (
                     <>
                     <DesktopRow
                        selected={isChecked}
                        $col={grid}
                     >
                        {checkboxSelection && (
                           <CheckboxCell>
                              <Checkbox
                                 type="checkbox"
                                 checked={isChecked}
                                 onChange={() => checkboxSelection.onToggle(v._id)}
                              />
                           </CheckboxCell>
                        )}
                        <div onClick={() => handleItemChecked(item)}>
                           {v.code !== undefined && (
                              <DataItem type="title" $textbold={true} value={v.code} />
                           )}
                        </div>
                        {v.store !== undefined && (
                           <DataItem type="normal" value={v.store} />
                        )}
                        {v.price !== undefined && (
                           <DataItem
                              type="title"
                              $textbold={true}
                              value={currencyFormat(v.price, true)}
                           />
                        )}
                        <DataItem type="normal" value={dateLabel} />
                        {v.sold !== undefined && (
                           <DataItem
                              type="badge"
                              value={{
                                 color: v.sold ? 'DANGER' : 'SUCCESS',
                                 text: v.sold ? 'Sold' : 'Available',
                              }}
                           />
                        )}
                        <DataItem
                           type="more-action"
                           value={v._id.toString()}
                           dropdownData={dropdownData}
                           selected={item}
                        />
                     </DesktopRow>

                     <MobileCard>
                        <MobileCardTop>
                           {checkboxSelection && (
                              <MobileCheckbox>
                                 <Checkbox
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => checkboxSelection.onToggle(v._id)}
                                 />
                              </MobileCheckbox>
                           )}
                           <MobileCodeBlock>
                              <MobileCode>{v.code ?? ''}</MobileCode>
                              {v.store && <MobileTitleSm>{v.store}</MobileTitleSm>}
                           </MobileCodeBlock>
                           {v.sold !== undefined && (
                              <MobileBadge $sold={!!v.sold}>
                                 {v.sold ? 'Sold' : 'Available'}
                              </MobileBadge>
                           )}
                        </MobileCardTop>
                        <MobileCardMid>
                           <MobileMeta>
                              <span>{dateLabel}</span>
                           </MobileMeta>
                           {v.price !== undefined && (
                              <MobilePrice>{currencyFormat(v.price, true)}</MobilePrice>
                           )}
                        </MobileCardMid>
                        <MobileActionSlot>
                           <DataItem
                              type="more-action"
                              value={v._id.toString()}
                              dropdownData={dropdownData}
                              selected={item}
                           />
                        </MobileActionSlot>
                     </MobileCard>
                     </>
                     );
                  })()}
                  {type === 'transaction' && (() => {
                     const t = item as unknown as {
                        _id: string;
                        updatedAt?: string;
                        status?: string;
                        voucherCode?: string;
                        store?: string;
                        netAmount?: number;
                        customer?: string;
                        smsStatus?: string;
                        smsError?: string;
                     };
                     const isSuccess = t.status === 'success';
                     const smsLabel =
                        t.smsStatus === 'sent'
                           ? { color: 'SUCCESS', text: 'Sent' }
                           : t.smsStatus === 'failed'
                           ? { color: 'DANGER', text: 'Failed' }
                           : t.smsStatus === 'disabled'
                           ? { color: 'GRAY5', text: 'Off' }
                           : { color: 'GRAY5', text: '—' };
                     return (
                     <>
                     <DesktopRow
                        selected={itemChecked.some((i) => i._id === item._id)}
                        $col={grid}
                        onClick={() => onRowClick?.(item)}
                     >
                        {t.updatedAt !== undefined && (
                           <DataItem type="date" value={t.updatedAt} />
                        )}
                        {t.status !== undefined && (
                           <DataItem
                              type="badge"
                              value={{
                                 color: isSuccess ? 'SUCCESS' : 'GRAY5',
                                 text: t.status === 'pending' ? 'Pending' : 'Success',
                              }}
                           />
                        )}
                        {t.voucherCode !== undefined && (
                           <DataItem type="title" value={t.voucherCode} />
                        )}
                        {t.store !== undefined && (
                           <DataItem type="normal" value={t.store} />
                        )}
                        {t.netAmount !== undefined && (
                           <DataItem
                              type="title"
                              $textbold={true}
                              value={currencyFormat(t.netAmount, true)}
                           />
                        )}
                        {t.customer !== undefined && (
                           <DataItem type="normal" value={t.customer} />
                        )}
                        {t.smsStatus !== undefined && (
                           <DataItem
                              type="badge"
                              value={smsLabel}
                           />
                        )}
                        <DataItem
                           type="more-action"
                           value={t._id.toString()}
                           dropdownData={dropdownData}
                           selected={item}
                        />
                     </DesktopRow>

                     <MobileCard onClick={() => onRowClick?.(item)}>
                        <MobileCardTop>
                           <MobileCodeBlock>
                              <MobileCode>{t.voucherCode ?? ''}</MobileCode>
                              {t.store && <MobileTitleSm>{t.store}</MobileTitleSm>}
                           </MobileCodeBlock>
                           <MobileBadge $sold={!isSuccess}>
                              {t.status === 'pending' ? 'Pending' : 'Success'}
                           </MobileBadge>
                        </MobileCardTop>
                        <MobileCardMid>
                           <MobileMeta>
                              {t.updatedAt && (
                                 <span>
                                    <Moment fromNow>{new Date(t.updatedAt)}</Moment>
                                 </span>
                              )}
                              {t.customer && <MobileDot>·</MobileDot>}
                              {t.customer && <span>{t.customer}</span>}
                           </MobileMeta>
                           {t.netAmount !== undefined && (
                              <MobilePrice>{currencyFormat(t.netAmount, true)}</MobilePrice>
                           )}
                        </MobileCardMid>
                        <MobileActionSlot onClick={(e) => e.stopPropagation()}>
                           <DataItem
                              type="more-action"
                              value={t._id.toString()}
                              dropdownData={dropdownData}
                              selected={item}
                           />
                        </MobileActionSlot>
                     </MobileCard>
                     </>
                     );
                  })()}
               </div>
            );
         })}
         </Content>
         
      </Wrapper>
   );
};

const Wrapper = styled.div``;
const Content = styled.div`
   

`
const HeadWrapper = styled.div<{
   $col: string;
}>`
   border-bottom: 1px solid ${colors.GRAY13};
   display: grid;
   grid-template-columns: ${({ $col }) => $col};
   gap: 1rem;
   padding: 10px 1rem;
   @media (max-width: 768px) {
     display: none;
   }
`;

const StoreLogo = styled.div`
   width: 36px;
   height: 36px;
   border-radius: 8px;
   overflow: hidden;
   flex-shrink: 0;
`;

const StoreLogoImg = styled.img`
   width: 100%;
   height: 100%;
   object-fit: cover;
`;

const StoreLogoPlaceholder = styled.div`
   width: 100%;
   height: 100%;
   display: flex;
   align-items: center;
   justify-content: center;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
`;

const SetupBtn = styled.button`
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   border-radius: 5px;
   padding: 5px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   white-space: nowrap;
   transition: opacity 0.2s;

   &:hover {
      opacity: 0.85;
   }
`;

const TableData = styled.div<{
   $col: string;
   selected: boolean;
}>`
   position: relative;
   display: grid;
   grid-template-columns: ${({ $col }) => $col};
   gap: 1rem;
   padding: 8px 1rem;
   align-items: center;
   border-bottom: 1px solid ${colors.GRAY13};
   background: ${({ selected }) => (selected ? '#e5eefc7d' : 'none')};

   cursor: pointer;
   &:hover {
      background: ${({ selected }) =>
         selected ? '#e5eefc7d' : `${colors.GRAY14}`};
   }

   @media (max-width: 768px) {
     grid-template-columns: 1fr;
     border-bottom: none;
     box-shadow: ${colors.BOXSHADOW};
      margin-bottom: 1rem;
      border-radius: 4px;
   }
`;

const DesktopRow = styled(TableData)`
   @media (max-width: 768px) {
      display: none;
   }
`;

const MobileCard = styled.div`
   display: none;
   @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 10px 12px;
      margin: 8px 12px;
      background: var(--app-surface);
      border: 1px solid ${colors.GRAY13};
      border-radius: 10px;
      position: relative;
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s;
      &:active {
         background: ${colors.GRAY14};
      }
   }
`;

const MobileCardTop = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 8px;
   padding-right: 36px;
`;

const MobileCodeBlock = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
   flex: 1;
`;

const MobileCode = styled.span`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK90};
   line-height: 1.2;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const MobileTitleSm = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
   line-height: 1.2;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const MobileBadge = styled.span<{ $sold: boolean }>`
   display: inline-flex;
   align-items: center;
   flex-shrink: 0;
   font-size: 11px;
   font-weight: 600;
   padding: 3px 8px;
   border-radius: 999px;
   color: ${({ $sold }) => ($sold ? colors.DANGER : colors.SUCCESS)};
   background: ${({ $sold }) =>
      $sold ? 'rgba(220,53,69,0.1)' : 'rgba(25,135,84,0.1)'};
`;

const MobileCardMid = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
`;

const MobileMeta = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 12px;
   color: ${colors.GRAY5};
   min-width: 0;
   flex: 1;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
   }
`;

const MobileDot = styled.span`
   color: ${colors.GRAY10};
`;

const MobilePrice = styled.span`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.PRIMARY};
   flex-shrink: 0;
`;

const MobileActionSlot = styled.div`
   position: absolute;
   top: 8px;
   right: 8px;
`;

const StoreAvatar = styled.div`
   width: 36px;
   height: 36px;
   border-radius: 8px;
   overflow: hidden;
   flex-shrink: 0;
`;

const MobileDomain = styled.a`
   font-size: 12px;
   color: ${colors.PRIMARY};
   text-transform: lowercase;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   &:hover { text-decoration: underline; }
`;

const MobileStatsRow = styled.div`
   display: flex;
   gap: 12px;
   align-items: center;
   padding-top: 4px;
   border-top: 1px dashed ${colors.GRAY13};
`;

const Stat = styled.div<{ $right?: boolean }>`
   display: flex;
   flex-direction: column;
   gap: 2px;
   ${({ $right }) => $right && 'margin-left:auto; align-items:flex-end;'}
`;

const StatLabel = styled.span`
   font-size: 10px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
`;

const StatValue = styled.span`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.MAIN_DARK90};
`;

const SetupBtnFull = styled.button`
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   border-radius: 6px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   margin-top: 2px;
   &:active { opacity: 0.85; }
`;

const MobileStatusPill = styled.span<{ $variant: 'success' | 'danger' | 'neutral' }>`
   display: inline-flex;
   align-items: center;
   flex-shrink: 0;
   font-size: 11px;
   font-weight: 600;
   padding: 3px 8px;
   border-radius: 999px;
   color: ${({ $variant }) =>
      $variant === 'success'
         ? colors.SUCCESS
         : $variant === 'danger'
         ? colors.DANGER
         : colors.GRAY5};
   background: ${({ $variant }) =>
      $variant === 'success'
         ? 'rgba(25,135,84,0.1)'
         : $variant === 'danger'
         ? 'rgba(220,53,69,0.1)'
         : 'rgba(153,153,153,0.12)'};
`;

const MobileSubMeta = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const CheckboxCell = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
`;

const Checkbox = styled.input`
   width: 16px;
   height: 16px;
   cursor: pointer;
   accent-color: ${colors.PRIMARY};
   appearance: none;
   -webkit-appearance: none;
   border: 1.5px solid ${colors.GRAY12};
   border-radius: 3px;
   background: transparent;
   position: relative;
   transition: all 0.15s;

   &:checked {
      background: ${colors.PRIMARY};
      border-color: ${colors.PRIMARY};
   }

   &:checked::after {
      content: '';
      position: absolute;
      top: 1px;
      left: 4px;
      width: 5px;
      height: 9px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
   }
`;

const MobileCheckbox = styled.div`
   flex-shrink: 0;
   display: flex;
   align-items: center;
   padding-top: 2px;
`;

export default Table;
